import { CustomView } from '../services/soap/dataObjects/CustomView';

export interface QueryParamsLike {
  FILTER: { COL: Array<{ name: string; type: string; value?: string | number }> };
  FIELDS?: { field: string[] };
  ORDER?: { COL: Array<{ name: string; desc?: string }> };
}

/**
 * Builds QueryParams from a CustomView definition.
 * Used when applying a view selection (e.g. initial load, after create).
 *
 * @param view - The CustomView to apply
 * @param store - Optional store with UserId for isCurrentUser filters
 * @returns QueryParams with filters, FIELDS, and ORDER from the view definition
 */
export function applyViewSelection(
  view: CustomView,
  store?: { UserId?: string | number }
): QueryParamsLike {
  const updatedParams: QueryParamsLike = { FILTER: { COL: [] } };

  if (!view.definition) {
    updatedParams.FILTER.COL.push({ name: 'state', type: 'equals', value: view.id || '' });
    return updatedParams;
  }

  try {
    const definition = JSON.parse(view.definition);

    if (definition.columns?.length) {
      const fields = definition.columns
        .filter((col: { isVisible?: boolean }) => col.isVisible !== false)
        .map((col: { itemname?: string }) => col.itemname)
        .filter((itemname: string) => itemname != null && itemname !== '');
      if (fields.length > 0) updatedParams.FIELDS = { field: fields };
    }

    if (definition.sorting?.length) {
      updatedParams.ORDER = {
        COL: definition.sorting.map((sort: { itemname?: string; level?: string }) => {
          let sortName = sort.itemname || '';
          if (sortName.includes('|')) {
            const parts = sortName.split('|');
            sortName = parts[1]?.trim() || sortName;
          }
          return { name: sortName, desc: sort.level === 'Descending' ? 'true' : 'false' };
        }),
      };
    }

    (definition.filters || []).forEach(
      (filterDef: {
        name?: string;
        filterInfo?: { type?: string; value?: string; isCurrentUser?: boolean }[];
      }) => {
        const filterInfo = filterDef?.filterInfo?.[0];
        const isEmptyOp = filterInfo?.type === 'is_empty' || filterInfo?.type === 'is_not_empty';
        if (!isEmptyOp && !filterInfo?.value && filterInfo?.isCurrentUser !== true) return;

        let filterName = filterDef.name || '';
        if (filterName.includes('|')) filterName = filterName.split('|')[0]?.trim() || filterName;

        let filterType = filterInfo?.type || 'equals';
        if (filterInfo?.type === 'is_empty') filterType = 'empty';
        else if (filterInfo?.type === 'is_not_empty') filterType = 'empty';

        let filterValue = filterInfo?.value || '';
        if (filterInfo?.isCurrentUser && store) filterValue = String(store.UserId);

        updatedParams.FILTER.COL.push({ name: filterName, type: filterType, value: filterValue });
      }
    );
  } catch (e) {
    console.error('Error parsing view definition:', e);
  }

  updatedParams.FILTER.COL.push({ name: 'state', type: 'equals', value: view.id || '' });
  return updatedParams;
}
